import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { TranslatePipe } from '@ngx-translate/core'
import { PanelMenuModule } from '@openng/optimus-ui/panelmenu'
import { toSignal } from '@angular/core/rxjs-interop'
import Keycloak from 'keycloak-js'
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select'
import { FormsModule } from '@angular/forms'
import { Ripple } from '@openng/optimus-ui/ripple'
import { Router, RouterLink, RouterLinkActive } from '@angular/router'
import { MenuItem } from '@openng/optimus-ui/api'
import { lastValueFrom, map } from 'rxjs'
import { CacheService } from '../cache-service/cache.service'
import { User } from '../state/user'
import { LanguageService } from '../../core/language.service'
import { Keys } from '../cache-service/keys'
import { CacheType } from '../cache-service/cache-type.enum'
import { AdChatService, AppUserGetAccountRecLegalAccountStatusEnum, AppUserGetAccountRecUserTypeEnum } from 'api'
import { Badge } from '@openng/optimus-ui/badge'
import { FileLinkPipe } from '../file-link.pipe'
import { injectQuery } from '@tanstack/angular-query-experimental'

@Component({
	standalone: true,
	selector: 'app-sidebar',
	imports: [
		PanelMenuModule,
		Select,
		FormsModule,
		TranslatePipe,
		Ripple,
		RouterLink,
		RouterLinkActive,
		Badge,
		FileLinkPipe,
	],
	templateUrl: './sidebar.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './sidebar.css',
})
export class SidebarComponent {
	readonly #cacheService = inject(CacheService)
	readonly #keycloakService = inject(Keycloak)
	readonly user = inject(User)
	readonly langService = inject(LanguageService)
	readonly #router = inject(Router)
	readonly #adChatService = inject(AdChatService)

	roles =
		this.#keycloakService.realmAccess?.roles.filter(realmRole =>
			['SUPER_ADMIN', 'CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'].includes(
				realmRole
			)
		) || []
	role = toSignal(
		this.#cacheService
			.getItem<
				| 'SUPER_ADMIN'
				| 'CLINIC_ADMIN'
				| 'SALON_ADMIN'
				| 'SELLER_USER'
				| 'TRAINEE_USER'
				| 'TRAINER_USER'
				| 'USER'
				| null
			>(Keys.Role, CacheType.SESSION_STORAGE)
			.pipe(
				map(cachedRole => {
					// اگر role کش شده متعلق به یوزر فعلی نبود
					if (!cachedRole || !this.roles.includes(cachedRole)) {
						// اولین نقش معتبر یوزر فعلی
						const newRole = this.roles[0] || null

						// ذخیره role جدید
						if (newRole) {
							this.#cacheService.setItem(Keys.Role, newRole, CacheType.SESSION_STORAGE)
						}

						return newRole
					}

					return cachedRole
				})
			),
		{
			initialValue: this.roles[0] || null,
		}
	)

	selectedRole = signal<string | null>(null)

	constructor() {
		effect(() => {
			const currentRole = this.role()

			if (currentRole) {
				this.selectedRole.set(currentRole)
			}
		})
	}

	// adminPortfolioStatistics = injectQuery(() => ({
	// 	enabled: this.role() === 'ADMIN',
	// 	queryKey: ['admin-portfolio-statistics'],
	// 	queryFn: () => lastValueFrom(this.#portfolioService.getAdminRequestsStatistics()),
	// }))

	chatUnread = injectQuery(() => ({
		enabled: this.#keycloakService.authenticated,
		queryKey: ['chat-unread-summary'],
		queryFn: () => lastValueFrom(this.#adChatService.unreadSummary()),
		refetchInterval: 30000,
		refetchIntervalInBackground: false,
	}))

	_items = signal<MenuItemExtended[]>([
		// SUPER-ADMIN — گروه‌بندی per-نوع (فاز سازمان‌دهی پنل ادمین)
		{
			label: 'داشبورد مدیریت',
			icon: 'gauge',
			router: '/super-admin/admin-dashboard',
			roles: ['SUPER_ADMIN'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'بررسی و مجوز آگهی‌ها',
			icon: 'check-square',
			router: '/super-admin/accept-ad',
			roles: ['SUPER_ADMIN'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'اجاره فضا',
			icon: 'building',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'نوع فضا',
					icon: 'chart-scatter',
					router: '/super-admin/space-type',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'تخصص‌ها',
					icon: 'objects-column',
					router: '/super-admin/speciality-type',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'امکانات',
					icon: 'objects-column',
					router: '/super-admin/facility',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'محله‌ها',
					icon: 'map-marker',
					router: '/super-admin/neighborhood',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'قیمت ثبت و تمدید',
					icon: 'money-bill',
					router: '/super-admin/promotion-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'دوره آموزشی',
			icon: 'graduation-cap',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'مدیریت دوره‌ها',
					icon: 'video',
					router: '/super-admin/course-ads',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'تأیید مدرسان',
					icon: 'verified',
					router: '/super-admin/instructor-verification',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'قیمت ثبت و تمدید',
					icon: 'money-bill',
					router: '/super-admin/promotion-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'تجهیزات و مارکت‌پلیس',
			icon: 'shopping-bag',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'دیکشنری (دسته/برند/فیلد)',
					icon: 'tags',
					router: '/super-admin/marketplace-dict',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'نظرات مارکت‌پلیس',
					icon: 'comments',
					router: '/super-admin/marketplace-reviews',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'قیمت ثبت و تمدید',
					icon: 'money-bill',
					router: '/super-admin/promotion-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'مالی و ارتقا',
			icon: 'wallet',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'پلان‌های ارتقا و قوانین',
					icon: 'arrow-up',
					router: '/super-admin/promotion-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'کدهای تخفیف',
					icon: 'ticket',
					router: '/super-admin/coupon-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'گزارشات مالی',
					icon: 'money-bill',
					router: '/super-admin/payment-reports',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'کاربران و احراز هویت',
			icon: 'users',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'درخواست‌های نقش',
					icon: 'shield',
					router: '/super-admin/approval-list',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'مدیریت کاربران',
					icon: 'user-edit',
					router: '/super-admin/users-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'گزارشات و تبلیغات',
			icon: 'chart-bar',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'گزارشات آگهی',
					icon: 'chart-bar',
					router: '/super-admin/ad-reports',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'گزارشات گفتگو',
					icon: 'comments',
					router: '/super-admin/chat-reports',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'مدیریت بنرها',
					icon: 'image',
					router: '/super-admin/banner-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'گزارشات بنر',
					icon: 'chart-bar',
					router: '/super-admin/banner-report',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'بررسی نظرات',
					icon: 'eye',
					router: '/super-admin/review-moderation',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},
		{
			label: 'سیستم',
			icon: 'cog',
			roles: ['SUPER_ADMIN'],
			routerLinkActiveOptions: { exact: false },
			items: [
				{
					label: 'تنظیمات عمومی',
					icon: 'sliders-h',
					router: '/super-admin/app-settings',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'مدیریت الستیک‌سرچ',
					icon: 'database',
					router: '/super-admin/elastic-management',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
				{
					label: 'وزن‌های امتیاز',
					icon: 'star',
					router: '/super-admin/score-settings',
					roles: ['SUPER_ADMIN'],
					items: [],
					routerLinkActiveOptions: { exact: true },
				},
			],
		},

		// SALON-ADMIN
		{
			label: 'SIDEBAR.adTitle',
			icon: 'plus',
			router: '/salon-admin/ad',
			roles: ['SALON_ADMIN', 'CLINIC_ADMIN', 'SELLER_USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},

		// PUBLIC
		{
			label: 'SIDEBAR.adsList',
			icon: 'receipt',
			router: '/ads/adsList',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'SIDEBAR.myAds',
			icon: 'receipt',
			router: '/ads/my-ads',
			roles: ['SALON_ADMIN', 'CLINIC_ADMIN', 'SELLER_USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'پروفایل مدرس',
			icon: 'user',
			router: '/ads/instructor-profile',
			roles: ['TRAINER_USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'دوره‌های من',
			icon: 'receipt',
			router: '/ads/my-courses',
			roles: ['TRAINER_USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'تجهیزات من',
			icon: 'shopping-bag',
			router: '/ads/my-equipments',
			roles: ['SALON_ADMIN', 'CLINIC_ADMIN', 'SELLER_USER', 'TRAINER_USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			// Marketplace M5: escrow (secure) transactions — both sides of every deal.
			label: 'معاملات امن من',
			icon: 'shield',
			router: '/ads/secure-transactions',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'علاقه‌مندی‌ها',
			icon: 'heart',
			router: '/ads/favorites',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'نظرات من',
			icon: 'comments',
			router: '/ads/my-reviews',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'SIDEBAR.chat',
			icon: 'comments',
			router: '/chat/inbox',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'SIDEBAR.roleMake',
			icon: 'shield',
			router: '/request-role',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'SIDEBAR.profile',
			icon: 'user',
			router: '/ads/profile',
			roles: ['CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'SIDEBAR.profile',
			icon: 'user',
			router: '/super-admin/profile',
			roles: ['SUPER_ADMIN'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'راهنما و آموزش',
			icon: 'question-circle',
			router: '/guide',
			roles: ['SUPER_ADMIN', 'CLINIC_ADMIN', 'SALON_ADMIN', 'SELLER_USER', 'TRAINEE_USER', 'TRAINER_USER', 'USER'],
			items: [],
			routerLinkActiveOptions: { exact: true },
		},
	])
	// readonly badgeConfig = new Map<string, () => number | undefined>([
	// 	['SIDEBAR.legalUserManagement', () => this.adminPortfolioStatistics.data()?.legalRequestCount],
	// 	['wallet.moneyRequest', () => this.adminPortfolioStatistics.data()?.moneyRequestCount],
	// 	['wallet.moneyWalletPayById', () => this.adminPortfolioStatistics.data()?.chargeReceiptCount],
	// ])

	items = computed(() => {
		const unread = this.chatUnread.data()?.totalUnread ?? 0
		return this._items()
			.filter(item => item.roles.includes(this.selectedRole() || ''))
			.map(item => {
				if (item.label === 'SIDEBAR.chat' && unread > 0) {
					return { ...item, badgeCount: unread }
				}
				return item
			})
	})
	// private applyBadges(item: MenuItemExtended): MenuItemExtended {
	// 	const badgeSource = item.label ? this.badgeConfig.get(item.label) : undefined
	// 	const itemWithBadge = badgeSource ? { ...item, badgeCount: badgeSource() } : item
	//
	// 	if (itemWithBadge.items) {
	// 		return {
	// 			...itemWithBadge,
	// 			items: itemWithBadge.items.map(child => this.applyBadges(child as MenuItemExtended)),
	// 		}
	// 	}
	//
	// 	return itemWithBadge
	// }

	profileImageFileId = computed(() => this.user.getUser()().profileImageFileId)

	changeRole(event: SelectChangeEvent) {
		this.selectedRole.set(event.value)
		this.#cacheService.setItem(Keys.Role, event.value, CacheType.SESSION_STORAGE)
		this.#router.navigate(['/dashboard'])
	}

	protected readonly AppUserGetAccountRecLegalAccountStatusEnum = AppUserGetAccountRecLegalAccountStatusEnum
	protected readonly AppUserGetAccountRecUserTypeEnum = AppUserGetAccountRecUserTypeEnum
}

interface MenuItemExtended extends MenuItem {
	roles: string[]
	badgeCount?: number
}
